import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../reusable-components/AuthContext';
import { CurrentPageContext } from '../reusable-components/CurrentPageContext';
import { checkTokenAndRenderCode, isBlank } from '../utils/common-functions';
import { showSuccessMessage, showErrorMessage } from '../utils/toast-functions';
import { TextInput } from '../reusable-components/TextInput';
import {
  getAllUsers,
  addUser,
  deleteUser,
  getCurrentUser,
  updateUser,
} from '../services/user-api';
import { Slide, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DeleteIcon } from '../icons/DeleteIcon';
import { EditIcon } from '../icons/EditIcon';
import {
  GenericTable,
  ColumnDefinition,
} from '../reusable-components/GenericTable';
import { showOperationConfirmation } from '../reusable-components/ConfirmDialog';
import { UserValue } from '../models/user/UserValue';

// Create column definitions for the user table
const userColumnDefs: Record<string, ColumnDefinition> = {
  userId: {
    title: 'ID',
    index: 0,
    formatter: (value: number) => value.toString(),
  },
  login: {
    title: 'Username',
    index: 1,
    formatter: (value: string) => value,
  },
  email: {
    title: 'Email',
    index: 2,
    formatter: (value: string) => value,
  },
  active: {
    title: 'Active',
    index: 3,
    formatter: (value: boolean) => (value ? 'Yes' : 'No'),
  },
  admin: {
    title: 'Admin',
    index: 4,
    formatter: (value: boolean) => (value ? 'Yes' : 'No'),
  },
};

const UsersTable = GenericTable<UserValue>(userColumnDefs);

export const AdminPage: React.FC = () => {
  // State variables
  const [users, setUsers] = useState<UserValue[]>([]);
  const [showUsernameError, setShowUsernameError] = useState<boolean>(false);
  const [showPasswordError, setShowPasswordError] = useState<boolean>(false);
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [newUsername, setNewUsername] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newEmail, setNewEmail] = useState<string>('');
  const [newIsActive, setNewIsActive] = useState<boolean>(true);
  const [newIsAdmin, setNewIsAdmin] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isAdminUser, setIsAdminUser] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editUserPk, setEditUserPk] = useState<string>('');
  const [editPassword, setEditPassword] = useState<string>('');

  // Context and navigation
  const authContext = useContext(AuthContext);
  const { token } = authContext;
  const currentPageContext = useContext(CurrentPageContext);
  const { setCurrentRoute } = currentPageContext;
  const navigate = useNavigate();

  // Function to fetch all users
  const fetchUsers = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const fetchedUsers: UserValue[] = await getAllUsers(token);
      setUsers(fetchedUsers);
      setError('');
    } catch (err) {
      setError('Failed to fetch users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const hideAllValidationErrors = () => {
    setShowUsernameError(false);
    setShowPasswordError(false);
    setShowEmailError(false);
  };

  const cleanForm = () => {
    setNewUsername('');
    setNewPassword('');
    setNewEmail('');
    setNewIsActive(true);
    setNewIsAdmin(false);
    hideAllValidationErrors();
    setError('');
  };

  // Function to add a new user
  const handleAddUser = async () => {
    if (isBlank(newUsername) || isBlank(newPassword)) {
      setError('Username and password are required');
      return;
    }

    setLoading(true);
    try {
      hideAllValidationErrors();

      const success = await addUser(
        token,
        newUsername,
        newPassword,
        newEmail,
        newIsActive,
        newIsAdmin,
      );

      if (success) {
        showSuccessMessage(`User ${newUsername} added successfully`);
        setNewUsername('');
        setNewPassword('');
        setNewEmail('');
        setNewIsActive(true);
        setNewIsAdmin(false);
        fetchUsers();
      } else {
        showErrorMessage('Failed to add user');
      }
    } catch (err) {
      showErrorMessage('Failed to add user');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (token: string, user: UserValue) => {
    showOperationConfirmation(
      'Delete User',
      `Are you sure you want to delete user "${user.login}"?`,
      async () => {
        try {
          const success = await deleteUser(token, user.userPk);

          if (success) {
            showSuccessMessage(`User ${user.login} deleted successfully`);
            fetchUsers();
          } else {
            showErrorMessage('Failed to delete user');
          }
        } catch (err) {
          showErrorMessage('Failed to delete user');
          console.error(err);
        }
      },
    );
  };

  // Function to handle edit user
  const handleEditUser = async (_: string, user: UserValue) => {
    setIsEditMode(true);
    setEditUserPk(user.userPk);
    setNewUsername(user.login);
    setNewEmail(user.email || '');
    setEditPassword('');
    setNewIsActive(user.active);
    setNewIsAdmin(user.admin);
  };

  // Function to update an existing user
  const handleUpdateUser = async () => {
    if (isBlank(newUsername)) {
      setError('Username is required');
      return;
    }

    if (isBlank(newEmail) || !validEmail(newEmail)) {
      setError('Valid email is required');
      return;
    }

    setLoading(true);
    try {
      hideAllValidationErrors();

      const success = await updateUser(
        token,
        editUserPk,
        newUsername,
        editPassword,
        newEmail,
        newIsActive,
        newIsAdmin,
      );

      if (success) {
        showSuccessMessage(`User ${newUsername} updated successfully`);
        setIsEditMode(false);
        setEditUserPk('');
        cleanForm();
        fetchUsers();
      } else {
        showErrorMessage('Failed to update user');
      }
    } catch (err) {
      showErrorMessage('Failed to update user');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Function to cancel edit mode
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditUserPk('');
    cleanForm();
  };

  const checkIfAdmin = async () => {
    if (!token) return;
    try {
      const currentUser: UserValue | null = await getCurrentUser(token);
      if (currentUser && currentUser.admin) {
        setIsAdminUser(true);
      } else {
        setIsAdminUser(false);
      }
    } catch (err) {
      console.error('Error checking if user is admin:', err);
      setIsAdminUser(false);
    }
  };

  // Effect to check if user is admin and fetch users on component mount
  useEffect(() => {
    if (token) {
      checkIfAdmin();
      fetchUsers();
    }
  }, [token]);

  function validEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  return checkTokenAndRenderCode(
    token,
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Slide}
      />

      {isAdminUser ? (
        <div className="md:container md:mx-auto p-4">
          <h1 className="text-3xl font-bold mb-8">User Administration</h1>

          <div className="bg-base-100 shadow-md rounded p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">
              {isEditMode ? 'Edit User' : 'Add New User'}
            </h2>
            <div className="flex flex-wrap gap-4">
              <TextInput
                label="Username"
                value={newUsername}
                placeholder="Enter username"
                onChange={(e) => {
                  setNewUsername(e.target.value);
                  setShowUsernameError(true);
                }}
                required={true}
                showValidationError={showUsernameError}
                errorMessage="Username is required"
                disabled={isEditMode}
              />

              <TextInput
                label={
                  isEditMode
                    ? 'New Password (leave blank to keep current)'
                    : 'Password'
                }
                value={isEditMode ? editPassword : newPassword}
                placeholder={
                  isEditMode ? 'Enter new password' : 'Enter password'
                }
                onChange={(e) => {
                  isEditMode
                    ? setEditPassword(e.target.value)
                    : setNewPassword(e.target.value);
                  setShowPasswordError(true);
                }}
                fieldType="password"
                required={!isEditMode}
                showValidationError={showPasswordError && !isEditMode}
                errorMessage="Password is required"
              />

              <TextInput
                label="Email"
                value={newEmail}
                placeholder="Enter email"
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setShowEmailError(true);
                }}
                showValidationError={showEmailError}
                validation={validEmail}
                errorMessage="Invalid email format"
                required={true}
              />

              <div className="flex items-center gap-2">
                <label htmlFor="isActive">Active:</label>
                <input
                  id="isActive"
                  type="checkbox"
                  checked={newIsActive}
                  onChange={(e) => {
                    setNewIsActive(e.target.checked);
                  }}
                />
              </div>

              <div className="flex items-center gap-2">
                <label htmlFor="isAdmin">Admin:</label>
                <input
                  id="isAdmin"
                  type="checkbox"
                  checked={newIsAdmin}
                  onChange={(e) => {
                    setNewIsAdmin(e.target.checked);
                  }}
                />
              </div>

              <div className="w-full flex items-end gap-4">
                {isEditMode ? (
                  <>
                    <button
                      className="btn btn-primary"
                      onClick={handleUpdateUser}
                      disabled={
                        loading ||
                        isBlank(newUsername) ||
                        isBlank(newEmail) ||
                        !validEmail(newEmail)
                      }
                    >
                      {loading ? 'Updating...' : 'Update User'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      Cancel Edit
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn-primary"
                      onClick={handleAddUser}
                      disabled={
                        loading ||
                        isBlank(newUsername) ||
                        isBlank(newPassword) ||
                        isBlank(newEmail) ||
                        !validEmail(newEmail)
                      }
                    >
                      {loading ? 'Adding...' : 'Add User'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={cleanForm}
                      disabled={loading}
                    >
                      Clean Form
                    </button>
                  </>
                )}
              </div>
            </div>
            {error && (
              <div className="text-error mt-2" data-testid="error-message">
                {error}
              </div>
            )}
          </div>

          <div className="bg-base-100 shadow-md rounded p-6">
            <h2 className="text-xl font-semibold mb-4">Manage Users</h2>
            {loading ? (
              <div className="py-4 text-center">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="py-4 text-center">No users found</div>
            ) : (
              <UsersTable
                rows={users}
                listOfActions={[
                  [<EditIcon key="edit-icon" />, handleEditUser],
                  [<DeleteIcon key="delete-icon" />, handleDeleteUser],
                ]}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p>Only admin users can access this page.</p>
        </div>
      )}
    </>,
    setCurrentRoute,
    navigate,
  );
};
